import { z } from 'zod';
import { OperationalAwardTypeCode } from './awards.js';
import { Cents, PlainDate } from './common.js';
import { StaffContact } from './home.js';
import { ScanStatus } from './documents.js';
import { pageFields, PageInfo } from './paging.js';

const Ref = z.object({ id: z.string(), name: z.string() });
const PersonRef = Ref.extend({ initials: z.string() });
const AwardRef = z.object({ id: z.uuid(), displayId: z.string() });
const Count = z.number().int().nonnegative();
const DateTime = z.iso.datetime({ offset: true });
const Employee = z.object({ id: z.uuid(), name: z.string(), eeNumber: z.string().nullable() });

/** How a line is paid: the PAA choice (a)–(d), closed as cash after the deadline, or cash only. */
export const PaysAs = z.enum(['a', 'b', 'c', 'd', 'closed_as_cash', 'cash']);
export type PaysAs = z.infer<typeof PaysAs>;

/** Tax, the cash left to the employee and whole shares: the estimate, or Payroll's final. */
export const PayFigures = z.object({ taxCents: Cents, cashCents: Cents, shares: Count });
export type PayFigures = z.infer<typeof PayFigures>;

const Coding = z.object({ account: z.string(), project: z.string().nullable(), org: z.string() });

/** An earning statement on file for one person. */
const StatementFile = z.object({
  documentId: z.uuid(),
  fileName: z.string(),
  attachedAt: DateTime,
  attachedBy: Ref.nullable(),
});

/**
 * One person's line in the Payroll queue (P1): to pay (S8), paid with the statement still to
 * attach, or done today. Payroll never sees options-only lines.
 */
export const PayrollRow = z.object({
  award: AwardRef,
  lineId: z.uuid(),
  type: OperationalAwardTypeCode,
  person: Employee,
  payAs: PaysAs,
  /** The award's cash for this person, before tax. */
  grossCashCents: Cents,
  /**
   * A PAA choice's figures from the Stock Admin team's ADP entry (APP-101); always an estimate.
   * Null on a cash-only line: Payroll sets its tax.
   */
  estimate: PayFigures.nullable(),
  /** Payroll's numbers, once paid. */
  final: PayFigures.nullable(),
  coding: Coding.nullable(),
  awardDate: PlainDate.nullable(),
  /**
   * APP-248: when its letter was released, which is when it reached Payroll. The Spot section
   * shows it (S8's "Letter released"); null for a line that never had a letter release.
   */
  releasedAt: DateTime.nullable(),
  paidAt: DateTime.nullable(),
  paidBy: Ref.nullable(),
  statement: StatementFile.extend({ scanStatus: ScanStatus }).nullable(),
  completedAt: DateTime.nullable(),
  canChangeStatement: z.boolean(),
  /** Whether the reader can mark it paid: Payroll; a System Admin only sees it (APP-134). */
  canPay: z.boolean(),
  status: z.enum(['to_pay', 'statement_needed', 'done']),
  /** The award's version, sent back with Pay. */
  version: z.number().int(),
});
export type PayrollRow = z.infer<typeof PayrollRow>;

export const PayrollQueue = z.object({
  rows: z.array(PayrollRow),
  page: PageInfo,
  asOf: PlainDate,
  timeZone: z.string(),
  counts: z.object({ toPay: Count, statementsDue: Count }),
  /**
   * APP-248 (S8): the Spot Awards among the same lines, which the page shows in a section of their
   * own: its lines to pay and statements to attach (already within `counts`), and the awards still
   * to pay with their cash, for "3 awards · $2,750 · from the Spot Awards pool".
   */
  spot: z.object({
    toPay: Count,
    statementsDue: Count,
    awardsToPay: Count,
    cashToPayCents: Cents,
    /**
     * APP-267: the pools that pay that cash, largest share first; together they come to
     * `cashToPayCents`. An older Spot Award keeps the pool it was made from, and Settings can choose
     * another for new ones, so it's never assumed to be the Spot Awards pool.
     */
    paidFrom: z.array(z.object({ pool: Ref, cashCents: Cents })),
  }),
  /** An earning statement is required before a cash award closes (a setting). */
  statementRequired: z.boolean(),
  /**
   * The active Stock Admin team, by name and job title (APP-054's staff contact), for "Final say":
   * who agrees an override with Payroll.
   */
  stockAdmins: z.array(StaffContact),
});
export type PayrollQueue = z.infer<typeof PayrollQueue>;

/** Bounded payroll work/history query. Calendar dates are inclusive and validated at entry. */
export const PayrollQueueQuery = z
  .object({
    view: z.enum(['in_progress', 'completed']).default('in_progress'),
    /**
     * APP-248: which of the same lines to list: all of them, the Spot Awards' or the other awards'
     * (S8's two sections). It narrows the list; it never adds a line.
     */
    group: z.enum(['all', 'spot', 'other']).default('all'),
    period: z
      .enum(['last_day', 'last_30_days', 'last_90_days', 'this_year', 'all_time', 'custom'])
      .default('last_30_days'),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    ...pageFields,
  })
  .strict()
  .refine(
    (q) => q.period !== 'custom' || (q.from !== undefined && q.to !== undefined && q.from <= q.to),
    { message: 'Choose a valid From and To date.' },
  );
export type PayrollQueueQuery = z.infer<typeof PayrollQueueQuery>;

/** Payroll pays one person (S8 → S9) with its final tax; the cash and shares follow from it. */
export const PayLine = z
  .object({ version: z.number().int().positive(), finalTaxCents: Cents.nonnegative() })
  .strict();
export type PayLine = z.infer<typeof PayLine>;

/**
 * The FMV a grant is entered at (QA-041): the one its award was worked out with, never today's
 * (awards keep their values), and the day that FMV took effect (null when it was the default).
 */
export const GrantFmv = z.object({ cents: Cents, effectiveFrom: PlainDate.nullable() });
export type GrantFmv = z.infer<typeof GrantFmv>;

/** One paid cash line in the Stock Admin's earning statement check (SA6). */
export const StatementRow = z.object({
  award: AwardRef,
  lineId: z.uuid(),
  type: OperationalAwardTypeCode,
  person: Employee,
  payAs: PaysAs,
  grossCashCents: Cents,
  /** The FMV the award was valued at: shares at this price, with the cash and tax, add up to the cash. */
  fmvCents: Cents.nullable(),
  /** A PAA choice's estimate (APP-101); null on a cash-only line, which has none. */
  estimate: PayFigures.nullable(),
  final: PayFigures,
  /** Which of Payroll's numbers differ from the estimate; null when there's no estimate. */
  differs: z.object({ tax: z.boolean(), cash: z.boolean(), shares: z.boolean() }).nullable(),
  options: Count,
  paidAt: DateTime,
  statement: StatementFile.nullable(),
  status: z.enum([
    'waiting_on_statement',
    'ready',
    'differs',
    /** Past the check: closed, or on to Shareworks. */
    'closed',
    'to_shareworks',
  ]),
  /** The Stock Admin recorded Payroll's numbers with a reason. */
  overridden: z.boolean(),
  /** Who is on this award's statement check now. */
  claimedBy: PersonRef.nullable(),
  version: z.number().int(),
});
export type StatementRow = z.infer<typeof StatementRow>;

export const StatementList = z.object({
  rows: z.array(StatementRow),
  /** Whether the reader checks statements: the Stock Admin team; a System Admin sees (APP-134). */
  processes: z.boolean(),
  counts: z.object({ differ: Count, missing: Count }),
  statementRequired: z.boolean(),
});
export type StatementList = z.infer<typeof StatementList>;

/**
 * The Stock Admin's statement check (S9). Confirm when the statement matches; when the share
 * count changed, record Payroll's numbers with a reason, agreed with Payroll (SA6 override).
 */
export const StatementDecision = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('confirm'), version: z.number().int().positive() }).strict(),
  z
    .object({
      decision: z.literal('override'),
      version: z.number().int().positive(),
      finalCashCents: Cents.nonnegative(),
      finalTaxCents: Cents.nonnegative(),
      finalShares: Count,
      reason: z.string().trim().min(1).max(2000),
      agreedWithPayroll: z.literal(true),
    })
    .strict(),
]);
export type StatementDecision = z.infer<typeof StatementDecision>;

export const StatementDecisionResult = z.object({ row: StatementRow });
export type StatementDecisionResult = z.infer<typeof StatementDecisionResult>;

/** A grant in Shareworks: a person's options, or the whole shares their election bought. */
export const ShareworksGrant = z.enum(['options', 'shares']);
export type ShareworksGrant = z.infer<typeof ShareworksGrant>;

/**
 * One grant to enter in Shareworks (SA5, PRD §13, APP-067 §6): a person's options, or the shares
 * their election bought, each its own row with its own check-off (who and when). Claims are per
 * person: claiming one of a person's rows claims both.
 */
export const ShareworksRow = z.object({
  award: AwardRef,
  lineId: z.uuid(),
  grant: ShareworksGrant,
  type: OperationalAwardTypeCode,
  person: Employee,
  /** Options, or whole shares (Payroll's final count). */
  quantity: Count,
  grantDate: PlainDate.nullable(),
  /** Years the options vest over (a setting); null for shares, which are owned outright. */
  vestingYears: Count.nullable(),
  /** The price basis: the award's own FMV. Null only for an award with no saved values. */
  fmv: GrantFmv.nullable(),
  /** Who is entering this person's grants now; a claim lapses after the timeout with no activity. */
  claimedBy: PersonRef.nullable(),
  claimedAt: DateTime.nullable(),
  yours: z.boolean(),
  /** Checked off already, while the person's other grant waits: who and when. */
  entered: z.object({ at: DateTime, by: Ref }).nullable(),
  /**
   * Entered in Shareworks before a System Admin reopened the award (APP-053): check Shareworks
   * before entering it again, so nobody grants it twice. Null for a row never entered.
   */
  enteredBefore: z.object({ at: DateTime, by: Ref.nullable() }).nullable().optional(),
});
export type ShareworksRow = z.infer<typeof ShareworksRow>;

/**
 * A grant on its way but not ready to enter yet: what it waits on. Options wait with the person's
 * cash (the election, Payroll, the statement check); shares wait for Payroll's final count.
 */
export const ShareworksNotReady = z.object({
  award: AwardRef,
  lineId: z.uuid(),
  grant: ShareworksGrant,
  type: OperationalAwardTypeCode,
  person: Employee,
  /** Options, or the shares the election chose (Payroll sets the final count); null until chosen. */
  quantity: Count.nullable(),
  /** The price basis it will be entered at: the award's own FMV. */
  fmv: GrantFmv.nullable(),
  waitingOn: z.enum(['election', 'payroll', 'statement_check']),
});
export type ShareworksNotReady = z.infer<typeof ShareworksNotReady>;

export const ShareworksWorklist = z.object({
  rows: z.array(ShareworksRow),
  /** Whether the reader enters grants: the Stock Admin team; a System Admin sees (APP-134). */
  processes: z.boolean(),
  /** Grants on their way, not ready to enter yet (the board's "Not ready"). */
  notReady: z.array(ShareworksNotReady),
  /** Minutes of no activity after which a claim lapses (a setting). */
  claimTimeoutMinutes: Count,
  /** Others working in the list right now. */
  working: z.array(z.object({ person: PersonRef, rows: Count })),
  /** Checked off today, newest first: which grant, who and when. */
  enteredToday: z.array(
    z.object({
      award: AwardRef,
      lineId: z.uuid(),
      grant: ShareworksGrant,
      quantity: Count,
      person: Ref,
      enteredAt: DateTime,
      enteredBy: Ref,
    }),
  ),
});
export type ShareworksWorklist = z.infer<typeof ShareworksWorklist>;

/** Claim or hand back people's rows (both of a person's grants go together). */
export const ShareworksLines = z.object({ lineIds: z.array(z.uuid()).min(1).max(200) }).strict();
export type ShareworksLines = z.infer<typeof ShareworksLines>;

/** Check off grants as entered in Shareworks, or undo a check-off made by mistake. */
export const ShareworksGrants = z
  .object({
    grants: z
      .array(z.object({ lineId: z.uuid(), grant: ShareworksGrant }).strict())
      .min(1)
      .max(400),
  })
  .strict();
export type ShareworksGrants = z.infer<typeof ShareworksGrants>;

export const ShareworksClaimResult = z.object({
  claimed: z.array(z.uuid()),
  /** Rows someone else holds, or that are no longer waiting for Shareworks. */
  taken: z.array(z.object({ lineId: z.uuid(), by: z.string().nullable() })),
});
export type ShareworksClaimResult = z.infer<typeof ShareworksClaimResult>;

export const ShareworksEnteredResult = z.object({
  entered: z.array(z.object({ lineId: z.uuid(), grant: ShareworksGrant })),
  /** People whose every grant is now entered: their award line closes. */
  closed: z.array(z.uuid()),
  worklist: ShareworksWorklist,
});
export type ShareworksEnteredResult = z.infer<typeof ShareworksEnteredResult>;
